import { Button, IconButton } from '@affine/component';
import type { AttachmentBlockModel } from '@blocksuite/affine/model';
import { PresentationIcon } from '@blocksuite/icons/rc';
import { LiveData, useLiveData } from '@toeverything/infra';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { AttachmentViewerProps } from '../types';
import { getAttachmentBlob } from '../utils';
import * as styles from './styles.css';

type HtmlAttachmentState = {
  status: 'idle' | 'loading' | 'ready' | 'error';
  src: string | null;
  error: Error | null;
};

export type HtmlAttachmentLabels = {
  close: string;
  present: string;
  enterFullscreen: string;
  open: string;
  loading: string;
  reload: string;
  failedToLoad: string;
  fullscreenFailed: string;
};

const defaultLabels: HtmlAttachmentLabels = {
  close: 'Close',
  present: 'Present',
  enterFullscreen: 'Enter fullscreen',
  open: 'Preview HTML',
  loading: 'Loading HTML attachment...',
  reload: 'Reload',
  failedToLoad: 'Unable to load this HTML attachment.',
  fullscreenFailed: 'Fullscreen is unavailable in this browser.',
};

type HtmlAttachmentFrameProps = {
  model: AttachmentBlockModel;
  title: string;
  className?: string;
  labels: HtmlAttachmentLabels;
  active: boolean;
};

function useHtmlAttachment(model: AttachmentBlockModel, active: boolean) {
  const [attempt, setAttempt] = useState(0);
  const objectUrlRef = useRef<string | null>(null);
  const [state, setState] = useState<HtmlAttachmentState>({
    status: 'idle',
    src: null,
    error: null,
  });
  const sourceId = useLiveData(
    useMemo(() => LiveData.fromSignal(model.props.sourceId$), [model])
  );

  useEffect(() => {
    let disposed = false;
    let objectUrl: string | null = null;

    if (!active) {
      setState({ status: 'idle', src: null, error: null });
      return;
    }

    setState({ status: 'loading', src: null, error: null });

    void getAttachmentBlob(model)
      .then(blob => {
        if (!blob) {
          throw new Error('The HTML attachment is unavailable.');
        }

        const nextObjectUrl = URL.createObjectURL(blob);
        if (disposed) {
          URL.revokeObjectURL(nextObjectUrl);
          return;
        }

        objectUrl = nextObjectUrl;
        objectUrlRef.current = objectUrl;
        setState({ status: 'loading', src: objectUrl, error: null });
      })
      .catch(error => {
        if (!disposed) {
          setState({
            status: 'error',
            src: null,
            error:
              error instanceof Error
                ? error
                : new Error('The HTML attachment could not be loaded.'),
          });
        }
      });

    return () => {
      disposed = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        if (objectUrlRef.current === objectUrl) {
          objectUrlRef.current = null;
        }
      }
    };
  }, [active, attempt, model, sourceId]);

  const reload = useCallback(() => setAttempt(value => value + 1), []);
  const markLoaded = useCallback((src: string) => {
    setState(current =>
      current.src === src ? { status: 'ready', src, error: null } : current
    );
  }, []);
  const markLoadError = useCallback((src: string) => {
    setState(current => {
      if (current.src !== src) return current;

      URL.revokeObjectURL(src);
      if (objectUrlRef.current === src) {
        objectUrlRef.current = null;
      }
      return {
        status: 'error',
        src: null,
        error: new Error('The HTML attachment could not be displayed.'),
      };
    });
  }, []);

  return { state, reload, markLoaded, markLoadError };
}

function HtmlAttachmentFrame({
  model,
  title,
  className,
  labels,
  active,
}: HtmlAttachmentFrameProps) {
  const { state, reload, markLoaded, markLoadError } = useHtmlAttachment(
    model,
    active
  );

  return (
    <div className={className}>
      {state.src ? (
        <iframe
          key={state.src}
          className={styles.iframe}
          title={title}
          src={state.src}
          sandbox="allow-scripts"
          referrerPolicy="no-referrer"
          onLoad={() => markLoaded(state.src!)}
          onError={() => markLoadError(state.src!)}
        />
      ) : null}
      {state.status === 'loading' ? (
        <div className={styles.status} role="status">
          {labels.loading}
        </div>
      ) : null}
      {state.status === 'error' ? (
        <div className={styles.status} role="alert">
          <div className={styles.errorMessage}>{labels.failedToLoad}</div>
          <Button onClick={reload}>{labels.reload}</Button>
        </div>
      ) : null}
    </div>
  );
}

export type HtmlAttachmentPreviewProps = AttachmentViewerProps & {
  className?: string;
  title?: string;
  labels?: Partial<HtmlAttachmentLabels>;
  onPresent?: () => void;
};

/**
 * Renders an HTML attachment in an opaque-origin iframe. Scripts may run for
 * interactive attachments, but cannot access the parent page, its cookies, or
 * its storage because `allow-same-origin` is intentionally absent.
 */
export function HtmlAttachmentPreview({
  model,
  className,
  title = 'HTML attachment preview',
  labels,
  onPresent,
}: HtmlAttachmentPreviewProps) {
  const mergedLabels = { ...defaultLabels, ...labels };
  const sourceId = useLiveData(
    useMemo(() => LiveData.fromSignal(model.props.sourceId$), [model])
  );
  const [active, setActive] = useState(false);

  useEffect(() => {
    setActive(false);
  }, [sourceId]);

  return (
    <div className={clsx(styles.preview, className)}>
      <HtmlAttachmentFrame
        model={model}
        title={title}
        labels={mergedLabels}
        className={styles.frame}
        active={active}
      />
      {!active ? (
        <div className={styles.status}>
          <Button onClick={() => setActive(true)}>{mergedLabels.open}</Button>
        </div>
      ) : null}
      {onPresent ? (
        <div className={styles.controls}>
          <IconButton
            icon={<PresentationIcon />}
            tooltip={mergedLabels.present}
            aria-label={mergedLabels.present}
            onClick={onPresent}
          />
        </div>
      ) : null}
    </div>
  );
}

export type HtmlAttachmentPresentationProps = AttachmentViewerProps & {
  className?: string;
  title?: string;
  labels?: Partial<HtmlAttachmentLabels>;
  onClose: () => void;
  onFullscreenChange?: (isFullscreen: boolean) => void;
};

/**
 * A full-window HTML attachment presentation. Native fullscreen is requested
 * on this outer container only; the embedded document receives no fullscreen
 * capability through its iframe permissions.
 */
export function HtmlAttachmentPresentation({
  model,
  className,
  title = 'HTML attachment presentation',
  labels,
  onClose,
  onFullscreenChange,
}: HtmlAttachmentPresentationProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const mergedLabels = { ...defaultLabels, ...labels };
  const [fullscreenFailed, setFullscreenFailed] = useState(false);

  const exit = useCallback(() => {
    if (document.fullscreenElement === rootRef.current) {
      void document.exitFullscreen().catch(console.error);
    }
    onClose();
  }, [onClose]);

  const enterFullscreen = useCallback(() => {
    const root = rootRef.current;
    if (!root?.requestFullscreen) {
      setFullscreenFailed(true);
      return;
    }

    setFullscreenFailed(false);
    void root.requestFullscreen().catch(() => setFullscreenFailed(true));
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreen = document.fullscreenElement === rootRef.current;
      if (isFullscreen) setFullscreenFailed(false);
      onFullscreenChange?.(isFullscreen);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      if (document.fullscreenElement === rootRef.current) {
        void document.exitFullscreen().catch(console.error);
      }
    };
  }, [onClose, onFullscreenChange]);

  return (
    <div ref={rootRef} className={clsx(styles.presentation, className)}>
      <HtmlAttachmentFrame
        model={model}
        title={title}
        labels={mergedLabels}
        className={styles.presentationFrame}
        active={true}
      />
      <div className={styles.presentationControls}>
        <Button onClick={enterFullscreen}>
          {mergedLabels.enterFullscreen}
        </Button>
        <Button data-attachment-presentation-close onClick={exit}>
          {mergedLabels.close}
        </Button>
      </div>
      {fullscreenFailed ? (
        <div className={styles.presentationError} role="alert">
          {mergedLabels.fullscreenFailed}
        </div>
      ) : null}
    </div>
  );
}
